import { useEffect, useState } from 'react'
import {
  Box,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Chip,
  Grid,
  Divider,
  TextField,
  Button,
  Snackbar,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  IconButton,
  Dialog,
  Card,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material'
import api from '../api/client'

interface AdminSettingsData {
  institution?: { name?: string; tenant_id?: string }
  theme?: Record<string, any>
  portal?: Record<string, any>
  i18n?: Record<string, any>
  user_roles?: { admins?: string[]; operators?: string[] }
  misc?: Record<string, any>
  auth?: Record<string, any>
  sso_providers?: SsoProviderData[]
}

interface SsoProviderData {
  id: string
  type: string
  display_name: string
  issuer?: string
  authorization_endpoint?: string
  token_endpoint?: string
  userinfo_endpoint?: string
  end_session_endpoint?: string
  client_id?: string
  client_secret?: string
  scopes?: string[]
  claim_mappings?: Record<string, string>
  default_domain?: string
  broker_domain?: string
  tenant_id?: string
  auto_provision?: boolean
  default_groups?: string[]
  icon_url?: string
}

function ColorField({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  disabled?: boolean
}) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
      <Box
        sx={{
          width: 24,
          height: 24,
          borderRadius: 0.5,
          bgcolor: value || '#cccccc',
          border: '1px solid rgba(0,0,0,0.15)',
          flexShrink: 0,
        }}
      />
      <TextField
        label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        size="small"
        fullWidth
        placeholder="#003399"
      />
    </Box>
  )
}

function ReadonlyField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="body1" sx={{ fontWeight: 500 }}>
        {value || '—'}
      </Typography>
    </Box>
  )
}

export default function AdminSettingsPage() {
  const { t } = useTranslation()
  const [settings, setSettings] = useState<AdminSettingsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [editMode, setEditMode] = useState(false)
  const [saving, setSaving] = useState(false)
  const [activeTab, setActiveTab] = useState(0)
  const [snack, setSnack] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  })

  // SSO providers state
  const [ssoProviders, setSsoProviders] = useState<SsoProviderData[]>([])
  const [providerDialogOpen, setProviderDialogOpen] = useState(false)
  const [editingProvider, setEditingProvider] = useState<SsoProviderData | null>(null)
  const [providerForm, setProviderForm] = useState<SsoProviderData>({
    id: '', type: 'oidc', display_name: '', issuer: '', authorization_endpoint: '',
    token_endpoint: '', userinfo_endpoint: '', end_session_endpoint: '',
    client_id: '', client_secret: '', scopes: ['openid', 'profile', 'email'],
    claim_mappings: { username: 'preferred_username', display_name: 'name', email: 'email' },
    default_domain: '', broker_domain: 'dcloud.local', tenant_id: '',
    auto_provision: true, default_groups: ['{tenant_id}_All_Users'],
  })

  const [form, setForm] = useState<{
    institutionName: string
    primaryColor: string
    secondaryColor: string
    topbarColor: string
    topbarTextColor: string
    sidebarColor: string
    sidebarTextColor: string
    contentBackgroundColor: string
    glassmorphism: string
    backgroundMode: string
    backgroundImages: string
    logoUrl: string
    favicon: string
    loginBoxTitle: string
    loginBoxMessage: string
    iconMode: string
    defaultDomain: string
    defaultLanguage: string
    forceHtml5: string
    sessionTimeout: string
    twoFactor: string
    flexilabs_admin_group: string
  }>({
    institutionName: '',
    primaryColor: '',
    secondaryColor: '',
    topbarColor: '',
    topbarTextColor: '',
    sidebarColor: '',
    sidebarTextColor: '',
    contentBackgroundColor: '',
    glassmorphism: 'false',
    backgroundMode: 'solid',
    backgroundImages: '',
    logoUrl: '',
    favicon: '',
    loginBoxTitle: '',
    loginBoxMessage: '',
    iconMode: 'generic',
    defaultDomain: '',
    defaultLanguage: '',
    forceHtml5: 'false',
    sessionTimeout: '240',
    twoFactor: 'false',
    flexilabs_admin_group: '',
  })

  useEffect(() => {
    loadSettings()
  }, [])

  const loadSettings = () => {
    setLoading(true)
    api
      .get('/settings/admin')
      .then((res) => {
        const data: AdminSettingsData = res.data
        setSettings(data)
        setSsoProviders(data.sso_providers || [])
        syncForm(data)
      })
      .catch(() => setSettings(null))
      .finally(() => setLoading(false))
  }

  const syncForm = (data: AdminSettingsData) => {
    const theme = data.theme || {}
    const portal = data.portal || {}
    const institution = data.institution || {}
    const bgMode = theme.background_mode || 'solid'
    const bgImages = (theme.background_images || []).join(', ')
    setForm({
      institutionName: institution.name || '',
      primaryColor: theme.primary_color || '',
      secondaryColor: theme.secondary_color || '',
      topbarColor: theme.topbar_color || '',
      topbarTextColor: theme.topbar_text_color || '',
      sidebarColor: theme.sidebar_color || '',
      sidebarTextColor: theme.sidebar_text_color || '',
      contentBackgroundColor: theme.content_background_color || '',
      glassmorphism: String(theme.glassmorphism ?? false),
      backgroundMode: ['solid', 'image'].includes(bgMode) ? bgMode : 'solid',
      backgroundImages: bgImages,
      logoUrl: theme.logo_url || '',
      favicon: theme.favicon || '',
      loginBoxTitle: theme.login_box_title || '',
      loginBoxMessage: theme.login_box_message || '',
      iconMode: theme.icon_mode || 'generic',
      defaultDomain: portal.default_domain || '',
      defaultLanguage: portal.default_language || '',
      forceHtml5: String(portal.force_html5 ?? false),
      sessionTimeout: String(portal.session_timeout || 240),
      twoFactor: String(data.auth?.two_factor ?? false),
      flexilabs_admin_group: data.auth?.flexilabs_admin_group || '',
    })
  }

  const handleChange = (field: keyof typeof form, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  const handleSave = async () => {
    setSaving(true)
    const payload: Record<string, any> = {
      theme: {
        primary_color: form.primaryColor,
        secondary_color: form.secondaryColor,
        topbar_color: form.topbarColor,
        topbar_text_color: form.topbarTextColor,
        sidebar_color: form.sidebarColor,
        sidebar_text_color: form.sidebarTextColor,
        content_background_color: form.contentBackgroundColor,
        glassmorphism: form.glassmorphism === 'true',
        background_mode: form.backgroundMode,
        background_images: form.backgroundImages
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
        logo_url: form.logoUrl,
        favicon: form.favicon,
        login_box_title: form.loginBoxTitle,
        login_box_message: form.loginBoxMessage,
        icon_mode: form.iconMode,
      },
      institution: {
        name: form.institutionName,
      },
      portal: {
        default_domain: form.defaultDomain,
        default_language: form.defaultLanguage,
        force_html5: form.forceHtml5 === 'true',
        session_timeout: parseInt(form.sessionTimeout, 10) || 240,
      },
      auth: {
        two_factor: form.twoFactor === 'true',
        flexilabs_admin_group: form.flexilabs_admin_group,
      },
    }

    try {
      await api.put('/settings/admin', payload)
      setSnack({ open: true, message: t('admin.saved') || 'Saved successfully', severity: 'success' })
      setEditMode(false)
      loadSettings()
    } catch (err: any) {
      const detail = err.response?.data?.detail || t('admin.saveError') || 'Failed to save'
      setSnack({ open: true, message: detail, severity: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    if (settings) syncForm(settings)
    setEditMode(false)
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!settings) {
    return <Alert severity="error">Failed to load admin settings.</Alert>
  }

  const theme = settings.theme || {}
  const portal = settings.portal || {}
  const institution = settings.institution || {}

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" gutterBottom>
          {t('navigation.adminSettings')}
        </Typography>
        {!editMode ? (
          <Button variant="contained" onClick={() => setEditMode(true)}>
            {t('admin.edit') || 'Edit'}
          </Button>
        ) : (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" onClick={handleCancel} disabled={saving}>
              {t('admin.cancel') || 'Cancel'}
            </Button>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? <CircularProgress size={20} color="inherit" /> : t('admin.save') || 'Save'}
            </Button>
          </Box>
        )}
      </Box>

      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
      >
        <Tab label={t('admin.tabs.branding') || 'Branding'} />
        <Tab label={t('admin.tabs.portal') || 'Portal'} />
        <Tab label={t('admin.tabs.security') || 'Security'} />
        <Tab label={t('admin.tabs.integrations') || 'Integrations'} />
      </Tabs>

      {/* ── Tab 0: Branding ── */}
      {activeTab === 0 && (
        <Paper sx={{ p: 3 }}>
          <Grid container spacing={2}>
            {editMode ? (
              <>
                <Grid item xs={12} sm={6} md={4}>
                  <ColorField label={t('admin.primaryColor') || 'Primary Color'} value={form.primaryColor} onChange={(v) => handleChange('primaryColor', v)} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <ColorField label={t('admin.secondaryColor') || 'Secondary Color'} value={form.secondaryColor} onChange={(v) => handleChange('secondaryColor', v)} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <ColorField label={t('admin.topbarColor') || 'Top Bar Color'} value={form.topbarColor} onChange={(v) => handleChange('topbarColor', v)} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <ColorField label={t('admin.topbarTextColor') || 'Top Bar Text Color'} value={form.topbarTextColor} onChange={(v) => handleChange('topbarTextColor', v)} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <ColorField label={t('admin.sidebarColor') || 'Sidebar Color'} value={form.sidebarColor} onChange={(v) => handleChange('sidebarColor', v)} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <ColorField label={t('admin.sidebarTextColor') || 'Sidebar Text Color'} value={form.sidebarTextColor} onChange={(v) => handleChange('sidebarTextColor', v)} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <ColorField label={t('admin.contentBackgroundColor') || 'Content Background Color'} value={form.contentBackgroundColor} onChange={(v) => handleChange('contentBackgroundColor', v)} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                    <InputLabel>{t('admin.glassmorphism') || 'Glassmorphism'}</InputLabel>
                    <Select
                      value={form.glassmorphism}
                      label={t('admin.glassmorphism') || 'Glassmorphism'}
                      onChange={(e) => handleChange('glassmorphism', e.target.value)}
                    >
                      <MenuItem value="true">Enabled</MenuItem>
                      <MenuItem value="false">Disabled</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                    <InputLabel>{t('admin.backgroundMode') || 'Background Mode'}</InputLabel>
                    <Select
                      value={form.backgroundMode}
                      label={t('admin.backgroundMode') || 'Background Mode'}
                      onChange={(e) => handleChange('backgroundMode', e.target.value)}
                    >
                      <MenuItem value="solid">Solid</MenuItem>
                      <MenuItem value="image">Image</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField label={t('admin.logoUrl') || 'Logo URL'} value={form.logoUrl} onChange={(e) => handleChange('logoUrl', e.target.value)} fullWidth size="small" sx={{ mb: 1.5 }} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField label={t('admin.favicon') || 'Favicon'} value={form.favicon} onChange={(e) => handleChange('favicon', e.target.value)} fullWidth size="small" sx={{ mb: 1.5 }} />
                </Grid>
                <Grid item xs={12}>
                  <TextField label={t('admin.backgroundImages') || 'Background Images (comma-separated)'} value={form.backgroundImages} onChange={(e) => handleChange('backgroundImages', e.target.value)} fullWidth size="small" sx={{ mb: 1.5 }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label={t('admin.loginBoxTitle') || 'Login Box Title'} value={form.loginBoxTitle} onChange={(e) => handleChange('loginBoxTitle', e.target.value)} fullWidth size="small" sx={{ mb: 1.5 }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label={t('admin.loginBoxMessage') || 'Login Box Message'} value={form.loginBoxMessage} onChange={(e) => handleChange('loginBoxMessage', e.target.value)} fullWidth size="small" sx={{ mb: 1.5 }} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                    <InputLabel>{t('admin.iconMode') || 'Icon Mode'}</InputLabel>
                    <Select
                      value={form.iconMode}
                      label={t('admin.iconMode') || 'Icon Mode'}
                      onChange={(e) => handleChange('iconMode', e.target.value)}
                    >
                      <MenuItem value="none">{t('admin.iconModeNone') || 'None'}</MenuItem>
                      <MenuItem value="generic">{t('admin.iconModeGeneric') || 'Generic'}</MenuItem>
                      <MenuItem value="actual">{t('admin.iconModeActual') || 'Actual'}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12} sm={6} md={4}>
                  <ReadonlyField label={t('admin.primaryColor') || 'Primary Color'} value={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 20, height: 20, borderRadius: 0.5, bgcolor: theme.primary_color }} />
                      {theme.primary_color}
                    </Box>
                  } />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <ReadonlyField label={t('admin.secondaryColor') || 'Secondary Color'} value={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 20, height: 20, borderRadius: 0.5, bgcolor: theme.secondary_color }} />
                      {theme.secondary_color}
                    </Box>
                  } />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <ReadonlyField label={t('admin.topbarColor') || 'Top Bar Color'} value={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 20, height: 20, borderRadius: 0.5, bgcolor: theme.topbar_color }} />
                      {theme.topbar_color}
                    </Box>
                  } />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <ReadonlyField label={t('admin.topbarTextColor') || 'Top Bar Text Color'} value={theme.topbar_text_color} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <ReadonlyField label={t('admin.sidebarColor') || 'Sidebar Color'} value={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 20, height: 20, borderRadius: 0.5, bgcolor: theme.sidebar_color }} />
                      {theme.sidebar_color}
                    </Box>
                  } />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <ReadonlyField label={t('admin.sidebarTextColor') || 'Sidebar Text Color'} value={theme.sidebar_text_color} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <ReadonlyField label={t('admin.contentBackgroundColor') || 'Content Background Color'} value={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 20, height: 20, borderRadius: 0.5, bgcolor: theme.content_background_color }} />
                      {theme.content_background_color}
                    </Box>
                  } />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <ReadonlyField label={t('admin.glassmorphism') || 'Glassmorphism'} value={theme.glassmorphism ? 'Enabled' : 'Disabled'} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <ReadonlyField label={t('admin.backgroundMode') || 'Background Mode'} value={theme.background_mode} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <ReadonlyField label={t('admin.logoUrl') || 'Logo URL'} value={theme.logo_url} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <ReadonlyField label={t('admin.favicon') || 'Favicon'} value={theme.favicon} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <ReadonlyField label={t('admin.loginBoxTitle') || 'Login Box Title'} value={theme.login_box_title} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <ReadonlyField label={t('admin.loginBoxMessage') || 'Login Box Message'} value={theme.login_box_message} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <ReadonlyField label={t('admin.iconMode') || 'Icon Mode'} value={theme.icon_mode} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <ReadonlyField label={t('admin.backgroundImages') || 'Background Images'} value={
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(theme.background_images || []).map((img: string) => (
                        <Chip key={img} label={img} size="small" variant="outlined" />
                      ))}
                    </Box>
                  } />
                </Grid>
              </>
            )}
          </Grid>
        </Paper>
      )}

      {/* ── Tab 1: Portal ── */}
      {activeTab === 1 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
            {t('admin.portal') || 'Portal'}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            {editMode ? (
              <>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField label={t('admin.defaultDomain') || 'Default Domain'} value={form.defaultDomain} onChange={(e) => handleChange('defaultDomain', e.target.value)} fullWidth size="small" sx={{ mb: 1.5 }} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                    <InputLabel>{t('admin.defaultLanguage') || 'Default Language'}</InputLabel>
                    <Select
                      value={form.defaultLanguage}
                      label={t('admin.defaultLanguage') || 'Default Language'}
                      onChange={(e) => handleChange('defaultLanguage', e.target.value)}
                    >
                      <MenuItem value="es-ES">Espanol</MenuItem>
                      <MenuItem value="en-US">English</MenuItem>
                      <MenuItem value="fr-FR">Français</MenuItem>
                      <MenuItem value="de-DE">Deutsch</MenuItem>
                      <MenuItem value="it-IT">Italiano</MenuItem>
                      <MenuItem value="ca-ES">Català</MenuItem>
                      <MenuItem value="eu-ES">Euskara</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                    <InputLabel>{t('admin.forceHtml5') || 'Force HTML5'}</InputLabel>
                    <Select
                      value={form.forceHtml5}
                      label={t('admin.forceHtml5') || 'Force HTML5'}
                      onChange={(e) => handleChange('forceHtml5', e.target.value)}
                    >
                      <MenuItem value="true">Yes</MenuItem>
                      <MenuItem value="false">No</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField label={t('admin.sessionTimeout') || 'Session Timeout (min)'} value={form.sessionTimeout} onChange={(e) => handleChange('sessionTimeout', e.target.value)} fullWidth size="small" sx={{ mb: 1.5 }} type="number" />
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12} sm={6} md={4}>
                  <ReadonlyField label={t('admin.defaultDomain') || 'Default Domain'} value={portal.default_domain} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <ReadonlyField label={t('admin.defaultLanguage') || 'Default Language'} value={portal.default_language || (settings.i18n || {}).default || 'es-ES'} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <ReadonlyField label={t('admin.forceHtml5') || 'Force HTML5'} value={portal.force_html5 ? 'Yes' : 'No'} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <ReadonlyField label={t('admin.sessionTimeout') || 'Session Timeout'} value={`${portal.session_timeout} min`} />
                </Grid>
              </>
            )}
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
            {t('admin.institution') || 'Institution'}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            {editMode ? (
              <Grid item xs={12} sm={6}>
                <TextField label={t('admin.institutionName') || 'Name'} value={form.institutionName} onChange={(e) => handleChange('institutionName', e.target.value)} fullWidth size="small" sx={{ mb: 1.5 }} />
              </Grid>
            ) : (
              <>
                <Grid item xs={12} sm={6} md={4}>
                  <ReadonlyField label={t('admin.institutionName') || 'Name'} value={institution.name} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <ReadonlyField label={t('admin.tenantId') || 'Tenant ID'} value={institution.tenant_id} />
                </Grid>
              </>
            )}
          </Grid>
        </Paper>
      )}

      {/* ── Tab 2: Security ── */}
      {activeTab === 2 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
            {t('admin.roles') || 'Roles'}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <ReadonlyField label={t('admin.admins') || 'Admins'} value={
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {(settings.user_roles?.admins || []).map((admin: string) => (
                <Chip key={admin} label={admin} size="small" color="primary" />
              ))}
            </Box>
          } />

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
            {t('admin.authentication') || 'Authentication'}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {editMode ? (
            <FormControlLabel
              control={
                <Switch
                  checked={form.twoFactor === 'true'}
                  onChange={(e) => handleChange('twoFactor', e.target.checked ? 'true' : 'false')}
                  color="primary"
                />
              }
              label={t('admin.twoFactor') || 'Require Two-Factor Authentication'}
            />
          ) : (
            <ReadonlyField
              label={t('admin.twoFactor') || 'Two-Factor Authentication'}
              value={settings.auth?.two_factor ? t('admin.enabled') || 'Enabled' : t('admin.disabled') || 'Disabled'}
            />
          )}

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
            {t('admin.security.flexilabsAdmins') || 'Flexilabs Admin Access'}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {editMode ? (
            <TextField
              fullWidth
              label={t('admin.security.flexilabsAdminGroup') || 'Flexilabs Admin Group (AD)'}
              value={form.flexilabs_admin_group ?? ''}
              onChange={(e) => handleChange('flexilabs_admin_group', e.target.value)}
              size="small"
              helperText={t('admin.security.flexilabsAdminGroupHint') || 'Members of this AD group always get admin role'}
            />
          ) : (
            <ReadonlyField
              label={t('admin.security.flexilabsAdminGroup') || 'Flexilabs Admin Group'}
              value={settings.auth?.flexilabs_admin_group || '—'}
            />
          )}
        </Paper>
      )}

      {/* ── Tab 3: Integrations ── */}
      {activeTab === 3 && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {t('admin.integrations.title') || 'SSO / Federation'}
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingProvider(null)
                setProviderForm({
                  id: '', type: 'oidc', display_name: '', issuer: '', authorization_endpoint: '',
                  token_endpoint: '', userinfo_endpoint: '', end_session_endpoint: '',
                  client_id: '', client_secret: '', scopes: ['openid', 'profile', 'email'],
                  claim_mappings: { username: 'preferred_username', display_name: 'name', email: 'email' },
                  default_domain: '', broker_domain: 'dcloud.local', tenant_id: '',
                  auto_provision: true, default_groups: ['{tenant_id}_All_Users'],
                })
                setProviderDialogOpen(true)
              }}
            >
              {t('admin.integrations.addProvider') || 'Add Provider'}
            </Button>
          </Box>

          {ssoProviders.length === 0 ? (
            <Alert severity="info">{t('admin.integrations.noProviders') || 'No SSO providers configured'}</Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {ssoProviders.map((p) => (
                <Card key={p.id} variant="outlined" sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle2">{p.display_name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {p.type.toUpperCase()} · {p.id} · {p.default_domain || '—'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Button size="small" onClick={() => {
                      setEditingProvider(p)
                      setProviderForm({ ...p, scopes: p.scopes || ['openid', 'profile', 'email'], claim_mappings: p.claim_mappings || {}, default_groups: p.default_groups || [] })
                      setProviderDialogOpen(true)
                    }}>Edit</Button>
                    <IconButton size="small" color="error" onClick={async () => {
                      const updated = ssoProviders.filter((sp) => sp.id !== p.id)
                      try {
                        await api.put('/settings/sso-providers', updated)
                        setSsoProviders(updated)
                        setSnack({ open: true, message: t('admin.saved'), severity: 'success' })
                      } catch { setSnack({ open: true, message: t('admin.saveError'), severity: 'error' }) }
                    }}><DeleteIcon fontSize="small" /></IconButton>
                  </Box>
                </Card>
              ))}
            </Box>
          )}
        </Paper>
      )}

      {/* SSO Provider Dialog */}
      <Dialog open={providerDialogOpen} onClose={() => setProviderDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingProvider ? t('admin.integrations.editProvider') || 'Edit Provider' : t('admin.integrations.addProvider') || 'Add Provider'}</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="ID (slug)" value={providerForm.id} onChange={(e) => setProviderForm((f) => ({ ...f, id: e.target.value }))} size="small" sx={{ mb: 2, mt: 1 }} disabled={!!editingProvider} />
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Type</InputLabel>
            <Select value={providerForm.type} onChange={(e) => setProviderForm((f) => ({ ...f, type: e.target.value }))} label="Type">
              <MenuItem value="oidc">OIDC</MenuItem>
              <MenuItem value="saml">SAML</MenuItem>
              <MenuItem value="cas">CAS</MenuItem>
            </Select>
          </FormControl>
          <TextField fullWidth label={t('admin.integrations.displayName')} value={providerForm.display_name} onChange={(e) => setProviderForm((f) => ({ ...f, display_name: e.target.value }))} size="small" sx={{ mb: 2 }} />
          {providerForm.type === 'oidc' && (
            <>
              <TextField fullWidth label={t('admin.integrations.issuer')} value={providerForm.issuer} onChange={(e) => setProviderForm((f) => ({ ...f, issuer: e.target.value }))} size="small" sx={{ mb: 2 }} />
              <TextField fullWidth label="Authorization URL" value={providerForm.authorization_endpoint} onChange={(e) => setProviderForm((f) => ({ ...f, authorization_endpoint: e.target.value }))} size="small" sx={{ mb: 2 }} />
              <TextField fullWidth label="Token URL" value={providerForm.token_endpoint} onChange={(e) => setProviderForm((f) => ({ ...f, token_endpoint: e.target.value }))} size="small" sx={{ mb: 2 }} />
              <TextField fullWidth label="Userinfo URL" value={providerForm.userinfo_endpoint} onChange={(e) => setProviderForm((f) => ({ ...f, userinfo_endpoint: e.target.value }))} size="small" sx={{ mb: 2 }} />
              <TextField fullWidth label="End Session URL" value={providerForm.end_session_endpoint} onChange={(e) => setProviderForm((f) => ({ ...f, end_session_endpoint: e.target.value }))} size="small" sx={{ mb: 2 }} />
              <TextField fullWidth label={t('admin.integrations.clientId')} value={providerForm.client_id} onChange={(e) => setProviderForm((f) => ({ ...f, client_id: e.target.value }))} size="small" sx={{ mb: 2 }} />
              <TextField fullWidth label={t('admin.integrations.clientSecret')} value={providerForm.client_secret} onChange={(e) => setProviderForm((f) => ({ ...f, client_secret: e.target.value }))} size="small" sx={{ mb: 2 }} type="password" />
              <TextField fullWidth label={t('admin.integrations.scopes')} value={(providerForm.scopes || []).join(', ')} onChange={(e) => setProviderForm((f) => ({ ...f, scopes: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) }))} size="small" sx={{ mb: 2 }} />
            </>
          )}
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>Provisioning</Typography>
          <TextField fullWidth label={t('admin.integrations.brokerDomain')} value={providerForm.broker_domain} onChange={(e) => setProviderForm((f) => ({ ...f, broker_domain: e.target.value }))} size="small" sx={{ mb: 2 }} />
          <TextField fullWidth label={t('admin.integrations.tenantId')} value={providerForm.tenant_id} onChange={(e) => setProviderForm((f) => ({ ...f, tenant_id: e.target.value }))} size="small" sx={{ mb: 2 }} />
          <FormControlLabel control={<Switch checked={providerForm.auto_provision} onChange={(e) => setProviderForm((f) => ({ ...f, auto_provision: e.target.checked }))} />} label={t('admin.integrations.autoProvision')} sx={{ mb: 2 }} />
          <TextField fullWidth label={t('admin.integrations.defaultGroups')} value={(providerForm.default_groups || []).join(', ')} onChange={(e) => setProviderForm((f) => ({ ...f, default_groups: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) }))} size="small" sx={{ mb: 2 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProviderDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={async () => {
            const updated = editingProvider
              ? ssoProviders.map((p) => p.id === editingProvider.id ? providerForm : p)
              : [...ssoProviders, providerForm]
            try {
              await api.put('/settings/sso-providers', updated)
              setSsoProviders(updated)
              setProviderDialogOpen(false)
              setSnack({ open: true, message: t('admin.saved'), severity: 'success' })
            } catch { setSnack({ open: true, message: t('admin.saveError'), severity: 'error' }) }
          }} disabled={!providerForm.id || !providerForm.display_name}>
            {t('admin.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={6000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}